import { QuoteLineItemProps } from '@partsbadger/types';

/**
 * A "variant" in the summary mirrors a Variant card in Parts & Specifications:
 * one material/finish/coating/process combination for a master product. When
 * the customer requested several quantities for the same variant, the backend
 * fans them out into sibling line items sharing a `quantity_group` — here we
 * fan them back in so the summary shows one card per variant with every
 * requested quantity listed underneath it, instead of one card per quantity.
 */
export interface SummaryVariantGroup {
    id: string;
    baseItem: QuoteLineItemProps;
    items: Array<QuoteLineItemProps>;
}

/** One master product ("Part N of M"), holding every variant quoted for it. */
export interface SummaryPartGroup {
    key: string;
    baseItem: QuoteLineItemProps;
    variants: Array<SummaryVariantGroup>;
}

const isMultiQuantityItem = (li: QuoteLineItemProps) =>
    !!li.quantity_group && !!li.needs_more_quantities && li.master_product_details.quotable && !li.has_dynamic_quantities;

/**
 * Groups a (already position-ordered) flat list of line items into parts ->
 * variants, matching the grouping used by the Parts & Specifications section
 * (see `MasterProduct.tsx`) so the Quote Summary can present the same
 * structure read-only.
 */
export const buildSummaryPartGroups = (lineItems: Array<QuoteLineItemProps>): Array<SummaryPartGroup> => {
    const masterProductBuckets = new Map<string, Array<QuoteLineItemProps>>();
    lineItems.forEach(li => {
        const key = String(li.product.master_product);
        const bucket = masterProductBuckets.get(key) ?? [];
        bucket.push(li);
        masterProductBuckets.set(key, bucket);
    });

    const groups: Array<SummaryPartGroup> = [];

    masterProductBuckets.forEach((items, key) => {
        const multiQuantityGroups = new Map<string, Array<QuoteLineItemProps>>();
        items.forEach(li => {
            if (isMultiQuantityItem(li)) {
                const groupId = li.quantity_group as string;
                const bucket = multiQuantityGroups.get(groupId) ?? [];
                bucket.push(li);
                multiQuantityGroups.set(groupId, bucket);
            }
        });

        // Prefer the newest row per quantity so transient duplicates from a
        // mid-sync quote do not surface twice (or with stale pricing) in the
        // summary.
        const canonicalGroupItems = new Map<string, Array<QuoteLineItemProps>>();
        multiQuantityGroups.forEach((bucket, groupId) => {
            const latestByQuantity = new Map<number, QuoteLineItemProps>();
            bucket.forEach(item => {
                const existing = latestByQuantity.get(item.quantity);
                if (!existing || item.id > existing.id) {
                    latestByQuantity.set(item.quantity, item);
                }
            });
            canonicalGroupItems.set(
                groupId,
                [...latestByQuantity.values()].sort((a, b) => a.quantity - b.quantity)
            );
        });

        const baseIdByGroup = new Map<string, number>();
        canonicalGroupItems.forEach((bucket, groupId) => {
            if (bucket[0]) {
                baseIdByGroup.set(groupId, bucket[0].id);
            }
        });

        const variants: Array<SummaryVariantGroup> = [];
        items.forEach(li => {
            if (isMultiQuantityItem(li)) {
                const groupId = li.quantity_group as string;
                if (li.id === baseIdByGroup.get(groupId)) {
                    const siblings = canonicalGroupItems.get(groupId) ?? [];
                    if (siblings.length > 0) {
                        variants.push({ id: groupId, baseItem: siblings[0], items: siblings });
                    }
                }
                return;
            }
            if (li.has_dynamic_quantities) {
                return;
            }
            if (li.master_product_details.quotable) {
                variants.push({ id: String(li.id), baseItem: li, items: [li] });
            }
        });

        groups.push({ key, baseItem: items[0], variants });
    });

    return groups;
};
